/**
 * hrisRoles — who can open which HRIS screens, maintained in the app instead
 * of in code.
 *
 * Access used to be two hard-coded lists in hrisAccess.ts: changing who could
 * see payroll meant a code change and a deploy. This puts the same decision
 * behind a shared store — HR assigns a role to an email, and every user picks
 * it up.
 *
 * SAFETY RULES, deliberately not configurable:
 *   · The built-in FULL-access accounts in hrisAccess.ts keep full access no
 *     matter what the shared config says. A bad edit cannot lock the owner out.
 *   · A missing or unreadable shared config falls back to exactly the previous
 *     hard-coded behaviour, so a SharePoint hiccup never widens OR removes
 *     access unexpectedly.
 *   · This still gates the APP UI only. SharePoint list permissions are the
 *     real boundary — see the note in hrisAccess.ts.
 */
import { createSharedSettingsStore } from "../../lib/sp/sharedSettingsStore";
import { type HrisScreenKey } from "./hrisSettings";
/* The catalogue and the resolution rules are pure and live next door so tests
 * can reach them without MSAL. Re-exported so every existing import works. */
export * from "./hrisRoleModel";
import {
  BUILT_IN_ROLES, grantFromConfig, normalise,
  type IHrisRole, type IHrisRoleConfig, type IHrisUserAssignment, type IResolvedGrant,
} from "./hrisRoleModel";

/* ── Shared store ─────────────────────────────────────────────────────────── */

const store = createSharedSettingsStore<IHrisRoleConfig>("iSyn_HrisRoles");
const LOCAL_KEY = "hris.roles";

type Listener = () => void;
const listeners = new Set<Listener>();

let cached: IHrisRoleConfig | null | undefined;

function readLocal(): IHrisRoleConfig | null {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (raw) return normalise(JSON.parse(raw) as Partial<IHrisRoleConfig>);
  } catch { /* ignore */ }
  return normalise(store.getCached());
}

/**
 * The configured roles and assignments, or null when nothing has been
 * configured — in which case callers keep the hard-coded behaviour.
 */
export function getRoleConfig(): IHrisRoleConfig | null {
  if (cached === undefined) cached = readLocal();
  return cached;
}

/** The catalogue to edit — the saved config, or the built-ins to start from. */
export const getRolesForEditing = (): IHrisRole[] => getRoleConfig()?.roles ?? BUILT_IN_ROLES;
export const getUsersForEditing = (): IHrisUserAssignment[] => getRoleConfig()?.users ?? [];

export function saveRoleConfig(config: IHrisRoleConfig): void {
  const next = normalise(config) ?? { roles: BUILT_IN_ROLES, users: [] };
  cached = next;
  try { localStorage.setItem(LOCAL_KEY, JSON.stringify(next)); } catch { /* quota */ }
  for (const l of listeners) l();
  store.saveShared(next).catch(() => undefined);
}

export function subscribeRoleConfig(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

let pulled = false;
/** Pull the org-wide role config once, then notify subscribers. */
export function pullSharedRoleConfig(): void {
  if (pulled) return;
  pulled = true;
  store.getShared().then((shared) => {
    const next = normalise(shared);
    if (!next) return;
    cached = next;
    try { localStorage.setItem(LOCAL_KEY, JSON.stringify(next)); } catch { /* quota */ }
    for (const l of listeners) l();
  }).catch(() => undefined);
}


/**
 * What the shared config grants this email, or null when it has no active
 * assignment — the caller then falls back to the hard-coded lists.
 */
export function grantFor(email: string | undefined): IResolvedGrant | null {
  return grantFromConfig(getRoleConfig(), email);
}
